import { useMemo, useState } from "react"
import { Folder } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Select } from "@/components/ui/select"
import { fmtCompactRelative } from "@/lib/format"
import { cn } from "@/lib/cn"
import type { Topic, TopicAgent } from "@/types/topic"

type SortKey = "lastUsed" | "convosDesc" | "nameAsc"

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "lastUsed",   label: "Last used" },
  { value: "convosDesc", label: "Most convos (30D)" },
  { value: "nameAsc",    label: "Name A→Z" },
]

type Props = {
  agents: TopicAgent[]
  selectedAgentId: number
  onAgentChange: (id: number) => void
  topics: Topic[]
  selectedId: number | null
  onSelect: (id: number) => void
  search: string
  onSearchChange: (v: string) => void
}

// Catalog column for the /topics page. Header (search + sort), then the
// table. Selection lifts the row into the right-side editor.
export function TopicList({
  agents, selectedAgentId, onAgentChange,
  topics, selectedId, onSelect,
  search, onSearchChange,
}: Props) {
  const [sort, setSort] = useState<SortKey>("lastUsed")

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return topics
    return topics.filter((t) => {
      return (
        t.name.toLowerCase().includes(q) ||
        t.key.toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q))
      )
    })
  }, [topics, search])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      if (sort === "nameAsc")    return a.name.localeCompare(b.name)
      if (sort === "convosDesc") return b.convoCount30d - a.convoCount30d
      // lastUsed (default): null → bottom; recency desc otherwise
      const av = a.lastUsedAt ? Date.parse(a.lastUsedAt) : 0
      const bv = b.lastUsedAt ? Date.parse(b.lastUsedAt) : 0
      return bv - av
    })
    return rows
  }, [filtered, sort])

  // Single agent in the dropdown still renders as a select for parity
  // with the rest of the portal, but it's locked when there's just one.
  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: a.id, label: a.name })),
    [agents],
  )
  const currentAgent = agentOptions.find((o) => o.value === selectedAgentId) ?? agentOptions[0]

  return (
    <Card className="flex h-full min-h-0 flex-col p-0">
      <header className="space-y-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Folder size={16} strokeWidth={2} className="text-app-muted" />
          <h2 className="text-base font-semibold text-app-text">Topic catalog</h2>
        </div>

        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search topics…"
          className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-muted focus:border-brand-red focus:outline-none"
        />

        {agents.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-app-muted">Agent</span>
            <div className="min-w-[160px]">
              <Select<number>
                value={currentAgent}
                options={agentOptions}
                onChange={(o) => o && onAgentChange(o.value)}
                isSearchable={agents.length > 6}
              />
            </div>
          </div>
        )}
      </header>

      <div className="flex items-center justify-between border-b border-border bg-app-panel2/30 px-4 py-2 text-[11px] text-app-muted">
        <div>{sorted.length} topic{sorted.length === 1 ? "" : "s"}</div>
        <div className="min-w-[180px]">
          <Select<SortKey>
            value={SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]}
            options={SORT_OPTIONS}
            onChange={(o) => o && setSort(o.value)}
            isSearchable={false}
          />
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-app-muted">
          {topics.length === 0 ? "No topics yet — click + New topic to add one." : "No matches."}
        </div>
      ) : (
        <div className="min-h-0 flex-1 lg:overflow-y-auto">
          <div className="hidden md:block">
            <ColumnHeader />
          </div>
          {sorted.map((t) => (
            <TopicRow
              key={t.id}
              topic={t}
              selected={t.id === selectedId}
              onClick={() => onSelect(t.id)}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

// Desktop: 4-col grid. Mobile: stacked card — name+folder on top, then
// the two stat cells render as inline "12 convos · 2d"
// strip below the description.
const ROW_GRID =
  "md:grid md:grid-cols-[28px_minmax(0,1.6fr)_96px_92px] md:items-center md:gap-3"

function ColumnHeader() {
  return (
    <div
      className={cn(
        ROW_GRID,
        "border-b border-border bg-app-panel2/20 px-4 py-2 text-[11px] uppercase tracking-wide text-app-muted",
      )}
    >
      <div />
      <div>Name</div>
      <div>Convos (30D)</div>
      <div className="text-right">Last used</div>
    </div>
  )
}

function TopicRow({ topic, selected, onClick }: {
  topic: Topic
  selected: boolean
  onClick: () => void
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick() } }}
      className={cn(
        ROW_GRID,
        "cursor-pointer border border-transparent border-b border-b-border/60 px-4 py-3 outline-none transition last:border-b-0",
        selected
          ? "rounded-control border-brand-red bg-brand-redSoft/20 shadow-[inset_0_0_0_1px_rgb(220_38_38)]"
          : "hover:bg-white/[0.02] focus-visible:bg-white/[0.04]",
      )}
    >
      {/* Mobile: folder + name share a flex row. */}
      <div className="flex items-start gap-3 md:contents">
        <div className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-brand-redSoft",
          selected ? "border-brand-red text-brand-red" : "border-brand-red/30 text-brand-red",
        )}>
          <Folder size={12} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-app-text">{topic.name}</div>
          <div className={cn(
            "truncate font-mono text-[11px]",
            selected ? "text-brand-red" : "text-app-muted",
          )}>{topic.key}</div>
          {topic.description && (
            <div className="mt-0.5 truncate text-xs text-app-muted">{topic.description}</div>
          )}
        </div>
      </div>
      {/* Stats — inline strip on mobile, separate cells on desktop. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-app-muted md:contents md:text-sm">
        <div className="md:text-app-text">
          <span className="md:hidden">Convos (30D): </span>
          <span className="tabular-nums">{topic.convoCount30d}</span>
        </div>
        <div className="md:text-right">
          <span className="md:hidden">Last used: </span>
          {topic.lastUsedAt ? fmtCompactRelative(topic.lastUsedAt) : "never"}
        </div>
      </div>
    </div>
  )
}
